import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router";
import { run } from "../../api";
import {
  fetchRule,
  fetchRules,
  fetchRulesetWithDefinitions,
} from "../registry/api";
import CodeEditor from "./components/CodeEditor";
import PatternEditor from "./components/PatternEditor";
import Results from "./components/Results";
import {
  ErrorType,
  Result,
  Rule,
  RuleDefinition,
  SemgrepErrors,
  Snippet,
} from "../../types";
import { DEFAULT_LANG, ERROR_TYPE_PATTERN, DEFAULT_PATTERN } from "../../util";
import { VISIBLE_LANGUAGES, Language } from "../../lang";
import { Highlight } from "../editor/types";
import { makeHighlight } from "../editor/util";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExpand,
  faExternalLinkAlt,
  faLock,
} from "@fortawesome/pro-solid-svg-icons";
import RunButton from "./components/RunButton";
import OutputButton from "./components/OutputButton";
import LanguageSelect from "./components/LanguageSelect";
import yaml from "yaml";
import SplitGutter from "../dashboard/workbench/components/SplitGutter";
import Split from "react-split-grid";
import { UserContext } from "../../contexts";

const EMPTY_HIGHLIGHTS: Highlight[] = [];

interface NullableSnippet {
  pattern: string;
  language: string;
  target?: string;
}

type Props = {
  registryId?: string;
  snippetId?: string;
  private?: boolean;
  // replace open link with call to this function
  // meant to expand the playground in a modal for better viewing
  expand?: () => void;
};

const Container = styled.div`
  background-color: #171f43;
  border: 1px solid #0f1446;
  border-radius: 5px;
  position: relative;
  height: 100%;
  min-height: 432px;
  width: 100%;
  line-height: 1.625;
  display: flex;
  flex-direction: column;
`;

const Grid = styled.div`
  display: grid;
  grid-template-rows: 1fr 4px 1fr;
  flex-grow: 1;
`;

const SplitSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const ResizeEditorWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const RulePanel = styled.section`
  padding-top: 4px;
  display: block;
  flex-grow: 1;
  position: relative;
`;

const RuleHeader = styled.div`
  width: 100%;
  color: #7a6bcb;
  font-size: var(--global-font-size-xs);
  padding: 4px;
  border-bottom: 1px solid #0f1446;
  position: relative;
`;

const LeftAlign = styled.div`
  position: absolute;
  right: 4px;
  top: 2px;
`;

const OpenLink = styled.a`
  margin-right: 4px;
  color: var(--global-green);
  :hover {
    color: var(--global-green);
  }
`;

const ExpandText = styled.div`
  margin-right: 4px;
  color: var(--global-green);
  :hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const CodeHeader = styled(RuleHeader)`
  border-top: 1px solid #0f1446;
`;

const CodePanel = styled.section`
  padding-top: 4px;
  display: block;
  margin-left: -20px;
  flex-grow: 1;
  position: relative;
`;

const ResultRow = styled(CodeHeader)`
  height: 32px;
  position: relative;
  border-bottom: none;
`;

const EditorWidget: React.FC<Props> = (props) => {
  const history = useHistory();
  // User should only set registry or snippet ID, but we're not enforcing that
  const urlParams = new URLSearchParams(history.location.search);
  const registryId = props.registryId ?? urlParams.get("registry");
  const snippetId = props.snippetId ?? urlParams.get("snippet");
  const [pattern, setPattern] = useState<string>(DEFAULT_PATTERN);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [language, setLanguage] = useState<Language>(DEFAULT_LANG);
  const [target, setTarget] = useState<string | undefined>(undefined);
  const [results, setResults] = useState<Result[]>();
  const [numTestFailures, setNumTestFailures] = useState<number>();
  const [errors, setErrors] = useState<SemgrepErrors>();
  const [errorType, setErrorType] = useState<ErrorType>();
  const [errorMsg, setErrorMsg] = useState<string>();
  const [highlighted, setHighlighted] = useState<Highlight[]>(EMPTY_HIGHLIGHTS);
  const [centeredLine, setCenteredLine] = useState<number>();
  const [showOutput, setShowOutput] = useState<boolean>(false);
  const { user } = useContext(UserContext);

  const highlightResults = (results: Result[], codeParseErrors: Result[]) => {
    if (codeParseErrors.length > 0) {
      const firstRes = codeParseErrors[0];
      // Make sure line is visible within editor (will scroll to it)
      setCenteredLine(firstRes.start.line);
      const highlighted = codeParseErrors.map((r) =>
        makeHighlight(r, true, "parseError")
      );
      setHighlighted(highlighted);
    } else if (results.length > 0) {
      const firstRes = results[0];
      // Make sure line is visible within editor (will scroll to it)
      setCenteredLine(firstRes.start.line);
      const highlighted = results.map((r) => makeHighlight(r, false));
      setHighlighted(highlighted);
    } else {
      setHighlighted(EMPTY_HIGHLIGHTS);
    }
  };

  const editorRun = (callback?: () => void) => {
    const target_sanitized = target?.replace(/\t/g, "    ");
    const snippet: NullableSnippet = {
      pattern,
      language,
      target: target_sanitized,
    };
    setIsRunning(true);
    setResults(undefined);
    setNumTestFailures(undefined);
    setErrors(undefined);
    setTarget(target_sanitized);

    if (pattern.trim().length === 0) {
      setErrorMsg("You can't run semgrep with an empty pattern");
      setIsRunning(false);
      setShowOutput(true);
    } else if (!snippet.target || snippet.target.trim().length === 0) {
      setErrorMsg("You can't run semgrep with an empty code block");
      setIsRunning(false);
      setShowOutput(true);
    } else {
      run(snippet as Snippet, true)
        .then((data) => {
          setResults(data.results);
          setNumTestFailures(data.num_test_failures);
          setErrors(data.errors);
          setErrorMsg(data.error_msg);
          setErrorType(data.error_type);
          setIsRunning(false);

          highlightResults(data.results, data.errors.parseCodeErrors);
          if (typeof callback === "function") {
            callback();
          }
          if (data.results.length === 0) {
            setShowOutput(true);
          }
        })
        .catch((err) => {
          setErrorMsg(`Error running semgrep: ${err.message}`);
          setShowOutput(true);
        });
    }
  };

  const runAsync = async (): Promise<void> => {
    return await new Promise((resolve) => {
      editorRun(() => {
        resolve();
      });
    });
  };

  const onLangChange = (language: Language) => {
    reset();
    setLanguage(language);
  };

  const loadFetchedSnippet = (rule: Rule & RuleDefinition) => {
    const test_case = rule.test_cases
      ? rule.test_cases[0]
      : {
          target: undefined,
          language: DEFAULT_LANG,
        };
    const snippet: NullableSnippet = {
      ...test_case,
      pattern: yaml.stringify(rule.definition),
      language: test_case.language || DEFAULT_LANG,
    };

    setPattern(snippet.pattern);
    setLanguage(
      snippet.language in VISIBLE_LANGUAGES
        ? (snippet.language as Language)
        : ("javascript" as Language)
    );
    setTarget(snippet.target);
  };

  const reset = () => {
    setHighlighted(EMPTY_HIGHLIGHTS);
    setShowOutput(false);
    if (!results || results.length === 0) {
      setResults(undefined);
      setErrorMsg(undefined);
    }
  };

  useEffect(() => {
    if (snippetId) {
      const promise = snippetId.includes(":")
        ? fetchRulesetWithDefinitions(snippetId).then((r) =>
            loadFetchedSnippet(r.rules[0])
          )
        : fetchRule(snippetId).then(loadFetchedSnippet);
      promise.catch((err) => {
        setErrorMsg(`Error loading snippet '${snippetId}': ${err.message}`);
        setShowOutput(true);
      });
    }

    if (registryId) {
      // loadRegistrySnippet
      const promise = fetchRules(registryId).then((r) =>
        loadFetchedSnippet(r[0])
      );
      promise.catch((err) => {
        setErrorMsg(`Error loading example '${registryId}': ${err.message}`);
        setShowOutput(true);
      });
    }
  }, [registryId, snippetId]);

  const isPatternError =
    errorType === ERROR_TYPE_PATTERN ? !!errorMsg : undefined;

  let openRuleLink = "";
  if (user) {
    openRuleLink = registryId
      ? `/orgs/-/editor/r/${registryId}`
      : `/orgs/-/editor/s/${snippetId}`;
  } else {
    openRuleLink = registryId
      ? `/playground/r/${registryId}`
      : `/playground/s/${snippetId}`;
  }

  return (
    <Container>
      <Split
        render={({ getGridProps, getGutterProps }: any) => (
          <Grid {...getGridProps()}>
            <SplitSection>
              <RuleHeader>
                <div>RULE</div>
                <LeftAlign>
                  {props.private ? (
                    <FontAwesomeIcon
                      icon={faLock}
                      style={{
                        color: "var(--global-green)",
                        marginRight: "4px",
                      }}
                    />
                  ) : props.expand ? (
                    <ExpandText onClick={props.expand}>
                      Expand rule <FontAwesomeIcon icon={faExpand} />
                    </ExpandText>
                  ) : (
                    <OpenLink
                      href={openRuleLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open in {user ? "Editor " : "Playground "}
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                    </OpenLink>
                  )}
                </LeftAlign>
              </RuleHeader>
              <RulePanel className="rule-editor">
                <ResizeEditorWrapper>
                  <PatternEditor
                    pattern={pattern}
                    onPatternChange={(newPattern) => {
                      reset();
                      setPattern(newPattern);
                    }}
                    error={isPatternError}
                  />
                </ResizeEditorWrapper>
              </RulePanel>
            </SplitSection>
            <SplitGutter between="top-bottom" {...getGutterProps("row", 1)} />
            <SplitSection>
              <CodeHeader>
                <div>TEST CODE</div>
                <LeftAlign>
                  <LanguageSelect onChange={onLangChange} value={language} />
                </LeftAlign>
              </CodeHeader>
              <CodePanel>
                <ResizeEditorWrapper>
                  <CodeEditor
                    // TODO: Make a real empty state
                    target={target ?? ""}
                    language={language}
                    highlighted={highlighted}
                    centeredLine={centeredLine}
                    onTargetChange={(newTarget) => {
                      reset();
                      setTarget(newTarget);
                    }}
                  />
                </ResizeEditorWrapper>
              </CodePanel>
            </SplitSection>
          </Grid>
        )}
      />
      {showOutput && (
        <Results
          results={results}
          errorMsg={errorMsg}
          pattern={pattern}
          language={language}
          errors={errors}
          numTestFailures={numTestFailures}
          loading={isRunning}
          onClose={() => setShowOutput(false)}
        />
      )}
      <ResultRow>
        <OutputButton
          active={showOutput}
          onClick={() => setShowOutput(!showOutput)}
          showSuccess={results !== undefined && results.length !== 0}
        />
        <RunButton onClick={runAsync} isRunning={isRunning} />
      </ResultRow>
    </Container>
  );
};

export default EditorWidget;
